import { Box, FormControl, Tabs, Tab } from "@mui/material";
import React from "react";

import { COLORS } from "../assets/theme";
import CustomTextField from "../components/CustomTextField";
import CustomButton from "../components/CustomButton";
import CustomSelect from "../components/CustomSelect";
import SearchHeaderContainer from "../components/table/SearchHeaderContainer";
import CustomTable from "../components/table/CustomTable";
import {
  sendAllNotification,
  sendNotificationToVenue,
  sendNotificationToOrganization,
  sendNotificationToClient,
  getAllNotifications,
} from "../services/NotificationService";
import { getUserId } from "../services/authService";
import { toast } from "react-toastify";
import { getAllBranches } from "../services/BranchesService";
import { getClientsPage } from "../services/ClientsService";
import { useRemoteSearchOptions } from "../hooks/useRemoteSearchOptions";
import { getAllCommunity } from "../services/CommunityService";
import { useLocation, useParams } from "react-router-dom";
import { useTabParam } from "../hooks/useTabParam";
import { getAllBusinesses } from "../services/VenueService";

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const showErrorToast = (message: any) =>
  toast.error(String(message), { toastId: String(message) });

const getItemId = (item: any): string | number =>
  item?.id ||
  item?._id ||
  item?.user_id ||
  item?.client_id ||
  item?.organizationUserId ||
  item?.organization_user_id ||
  item?.organization_user?.id ||
  "";

const getClientLabel = (item: any): string => {
  const name =
    item?.full_name ||
    item?.fullName ||
    item?.name ||
    [
      item?.first_name || item?.firstName,
      item?.last_name || item?.lastName,
    ]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    item?.user?.full_name ||
    item?.user?.fullName ||
    item?.user?.name ||
    `ID: ${getItemId(item)}`;

  const phone =
    item?.phone ||
    item?.mobile ||
    item?.phoneNumber ||
    item?.mobile_number ||
    item?.contact_number ||
    item?.number ||
    item?.tel ||
    item?.user?.phone ||
    item?.user?.mobile ||
    "No phone";

  return `${name} (${phone})`;
};

const UserNotifications: React.FC = () => {
  const location = useLocation();
    const params = useParams<{ businessId?: string; id?: string }>();
  const businessId = params.businessId || params.id;
  const { tab, setTab } = useTabParam([
    "user",
    "all-users",
    "venue",
    "organization",
  ]);
  const [search, setSearch] = React.useState<string>("");
  const [title, setTitle] = React.useState<string>("");
  const [message, setMessage] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [selectedBranch, setSelectedBranch] = React.useState<
    string | number | (string | number)[]
  >("");
  const [selectedTarget, setSelectedTarget] = React.useState<
    string | number | (string | number)[]
  >("");
  const [selectedAudience, setSelectedAudience] = React.useState<
    (string | number)[]
  >([]);
  const [audienceOptions, setAudienceOptions] = React.useState<any[]>([]);
  const [loadingAudience, setLoadingAudience] = React.useState(false);
  const fetchClientOptions = React.useCallback(async (term: string) => {
    const result = await getClientsPage({ page: 0, size: 30, search: term });
    return (result?.content ?? []).map((item: any) => ({
      value: getItemId(item),
      label: getClientLabel(item),
    }));
  }, []);
  const clientSearchOptions = useRemoteSearchOptions(fetchClientOptions, {
    enabled: tab === 0 && selectedTarget === "individualclient",
  });
  const [rows, setRows] = React.useState(() => {
    const total = 10;
    return Array.from({ length: total }, (_, i) => {
      const id = i + 1;
      const to = `Staff ${id}`;
      const message = `hii staff welcome here`;
      const sentDate = "23/8/2008";
      return { id, to, message, sentDate };
    });
  });
  const [notifications, setNotifications] = React.useState<any[]>([]);
  const [loadingNotifications, setLoadingNotifications] = React.useState(false);
  const [branches, setBranches] = React.useState<any[]>([]);
  const [loadingBranches, setLoadingBranches] = React.useState(false);
  const [venues, setVenues] = React.useState<any[]>([]);
  const [loadingVenues, setLoadingVenues] = React.useState(false);
  const [selectedVenue, setSelectedVenue] = React.useState<
    string | number | (string | number)[]
  >("");

  const [dateRange, setDateRange] = React.useState<
    { start?: string; end?: string } | [string?, string?] | null
  >(() => {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
    return { start: start.toISOString(), end: end.toISOString() };
  });

  const buildDateBoundary = (raw: string | Date | null | undefined, endOfDay: boolean) => {
    if (!raw) return null;
    if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split("-").map(Number);
      return new Date(Date.UTC(y, m - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0));
    }
    const parsed = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(
      Date.UTC(
        parsed.getUTCFullYear(),
        parsed.getUTCMonth(),
        parsed.getUTCDate(),
        endOfDay ? 23 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 999 : 0,
      ),
    );
  };

  const getNormalizedRange = (
    range: { start?: string; end?: string } | [string?, string?] | null
  ) => {
    if (!range) return { start: null as Date | null, end: null as Date | null };
    const startRaw = Array.isArray(range) ? range[0] : range.start;
    const endRaw = Array.isArray(range) ? range[1] : range.end;
    return {
      start: buildDateBoundary(startRaw as any, false),
      end: buildDateBoundary(endRaw as any, true),
    };
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const handleSendNotification = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error("Please fill in both title and message.");
      return;
    }

    if (tab === 2) {
      if (!selectedVenue) {
        toast.error("Please select a venue.");
        return;
      }
    } else if (tab === 1) {
      if (!selectedBranch) {
        toast.error("Please select a branch.");
        return;
      }
    }
    if (tab === 0) {
      if (!selectedTarget) {
        toast.error("Please select a target.");
        return;
      }

      if (!selectedAudience || selectedAudience.length === 0) {
        toast.error("Please select at least one audience.");
        return;
      }
    }

    const sender_id = getUserId();
    if (!sender_id) {
      toast.error("User not authenticated. Please log in again.");
      return;
    }

    setLoading(true);
    try {
      const payload: any = {
        message,
        body: message,
        sender_id,
        title,
      };

      if (tab === 2) {
        payload.businessId = selectedVenue;
      } else if (tab === 1) {
        payload.unitId = selectedBranch;
      } else {
        payload.admin_user_id = sender_id;
      }
      if (tab === 0 && selectedTarget && selectedAudience.length > 0) {
        if (selectedTarget === "individualclient") {
          payload.userIds = selectedAudience;
          payload.client_ids = selectedAudience;
        } else if (selectedTarget === "communitygroups") {
          payload.communityIds = selectedAudience;
          payload.group_id = selectedAudience;
        }
      }

      if (tab === 0) {
        await sendNotificationToClient(payload);
      } else if (tab === 1) {
        await sendAllNotification(payload);
      } else if (tab === 2) {
        await sendNotificationToVenue(payload);
      } else {
        await sendNotificationToOrganization(payload);
      }
      toast.success("Notification sent successfully!");
      fetchNotifications();
      setTitle("");
      setMessage("");
      setSelectedAudience([]);
    } catch (error: any) {
      showErrorToast(
        getErrorMessage(error, "Failed to send notification. Please try again.")
      );
      console.error("Send notification error:", error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    const fetchBranches = async () => {
      setLoadingBranches(true);
      try {
        if (!businessId) {
          console.error("No business ID available");
          setBranches([]);
          return;
        }

        const response = await getAllBranches(businessId);

        let branchesData = [];
        if (response && response.businesses) {
          branchesData = response.businesses;
        } else if (response && response.data) {
          branchesData = response.data;
        } else if (Array.isArray(response)) {
          branchesData = response;
        }

        setBranches(Array.isArray(branchesData) ? branchesData : []);
      } catch (error) {
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };

    fetchBranches();
  }, [businessId]);

  React.useEffect(() => {
    const fetchVenues = async () => {
      setLoadingVenues(true);
      try {
        const response = await getAllBusinesses();
        const venuesData = Array.isArray(response)
          ? response
          : Array.isArray((response as any)?.venues)
          ? (response as any).venues
          : Array.isArray((response as any)?.data)
          ? (response as any).data
          : [];

        setVenues(venuesData);
      } catch (error) {
        console.error("Error fetching venues:", error);
        setVenues([]);
        showErrorToast(getErrorMessage(error, "Failed to fetch venues."));
      } finally {
        setLoadingVenues(false);
      }
    };

    fetchVenues();
  }, []);

  React.useEffect(() => {
    const fetchAudience = async () => {
      if (tab !== 0 || !selectedTarget) {
        setAudienceOptions([]);
        return;
      }

      setLoadingAudience(true);
      try {
        if (selectedTarget === "individualclient") {
          // Clients are searched on the server as the user types.
          setAudienceOptions([]);
        } else if (selectedTarget === "communitygroups") {
          const response = await getAllCommunity();

          let communitiesData = [];
          if (response && response.groups) {
            communitiesData = response.groups;
          } else if (response && response.data) {
            communitiesData = response.data;
          } else if (Array.isArray(response)) {
            communitiesData = response;
          }
          setAudienceOptions(
            Array.isArray(communitiesData) ? communitiesData : []
          );
        }
      } catch (error) {
        console.error("Error fetching audience:", error);
        setAudienceOptions([]);
        showErrorToast(
          getErrorMessage(error, "Failed to fetch audience options.")
        );
      } finally {
        setLoadingAudience(false);
      }
    };

    fetchAudience();
  }, [tab, selectedTarget]);

  const fetchNotifications = React.useCallback(async () => {
    setLoadingNotifications(true);
    try {
      const { start, end } = getNormalizedRange(dateRange);
      const response = await getAllNotifications({
        ...(start ? { startDate: start.toISOString() } : {}),
        ...(end ? { endDate: end.toISOString() } : {}),
      });
      let notificationsData = [];
      if (response && response.notifications) {
        notificationsData = response.notifications;
      } else if (response && response.data) {
        notificationsData = response.data;
      } else if (Array.isArray(response)) {
        notificationsData = response;
      }
      setNotifications(
        Array.isArray(notificationsData) ? notificationsData : []
      );
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setNotifications([]);
    showErrorToast(getErrorMessage(error, "Failed to fetch notifications."));
    } finally {
      setLoadingNotifications(false);
    }
  }, [dateRange]);

  React.useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <Box sx={{ px: 2, pt: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Box
          sx={{
            width: { xs: "100%", sm: "100%" },
            p: { xs: 2, sm: 3 },
            pt: { xs: 4, sm: 5 },
            boxShadow: 2,
            borderRadius: 2,
            bgcolor: COLORS.WhiteBackground,
            position: "relative",
          }}
        >
          <Box sx={{ mb: { xs: 3, sm: 2 }, mt: { xs: -6, sm: -4 } }}>
            <Tabs
              value={tab}
              onChange={handleTabChange}
              aria-label="notification-tabs"
              sx={{
                "& .MuiTabs-flexContainer": {
                  justifyContent: { xs: "center", sm: "flex-start" },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: (t) => t.palette.primary.main,
                },
                borderRadius: "8px 8px 0 0",
                overflow: "hidden",
                mt: { xs: 2, sm: 0 },
              }}
            >
              <Tab
                label="Specific User"
                sx={{
                  textTransform: "none",
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  color: (t) => t.palette.text.secondary,
                  "&:hover": { bgcolor: "transparent" },
                }}
              />
              <Tab
                label="All Users"
                sx={{
                  textTransform: "none",
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  color: (t) => t.palette.text.secondary,
                  "&:hover": { bgcolor: "transparent" },
                }}
              />
              <Tab
                label="All Users in Venue"
                sx={{
                  textTransform: "none",
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  color: (t) => t.palette.text.secondary,
                  "&:hover": { bgcolor: "transparent" },
                }}
              />
              <Tab
                label="All Users in Organization"
                sx={{
                  textTransform: "none",
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  color: (t) => t.palette.text.secondary,
                  "&:hover": { bgcolor: "transparent" },
                }}
              />
            </Tabs>
          </Box>

          {tab === 2 ? (
            <>
              <Box sx={{ mb: 1.5 }}>
                <FormControl fullWidth>
                  <CustomSelect
                    fullWidth
                    label="Select Venue"
                    options={venues.map((venue) => ({
                      value: venue.id || venue._id,
                      label:
                        venue.name ||
                        venue.title ||
                        venue.fullName ||
                        `ID: ${venue.id || venue._id}`,
                    }))}
                    value={selectedVenue}
                    onChange={(value: string | number | (string | number)[]) =>
                      setSelectedVenue(value)
                    }
                    disabled={loadingVenues}
                  />
                </FormControl>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <CustomTextField
                  label={"Title"}
                  variant="outlined"
                  fullWidth
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <CustomTextField
                  label={"Message"}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </Box>

              <Box
                sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}
              >
                <CustomButton
                  label="Send Notification"
                  variant="contained"
                  sx={{ backgroundColor: "#1976d2" }}
                  onClick={handleSendNotification}
                  disabled={loading}
                />
              </Box>
            </>
          ) : tab === 1 ? (
            <>
              <Box sx={{ mb: 1.5 }}>
                <FormControl fullWidth>
                  <CustomSelect
                    fullWidth
                    label="Select Branch"
                    options={branches.map((branch) => ({
                      value: branch.id,
                      label: branch.name,
                    }))}
                    value={selectedBranch}
                    onChange={(value: string | number | (string | number)[]) =>
                      setSelectedBranch(value)
                    }
                    disabled={loadingBranches}
                  />
                </FormControl>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <CustomTextField
                  label={"Title"}
                  variant="outlined"
                  fullWidth
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <CustomTextField
                  label={"Message"}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </Box>

              <Box
                sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}
              >
                <CustomButton
                  label="Send Notification"
                  variant="contained"
                  sx={{ backgroundColor: "#1976d2" }}
                  onClick={handleSendNotification}
                  disabled={loading}
                />
              </Box>
            </>
          ) : tab === 3 ? (
            <>
              <Box sx={{ mb: 1.5 }}>
                <CustomTextField
                  label={"Title"}
                  variant="outlined"
                  fullWidth
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <CustomTextField
                  label={"Message"}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </Box>

              <Box
                sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}
              >
                <CustomButton
                  label="Send Notification"
                  variant="contained"
                  sx={{ backgroundColor: "#1976d2" }}
                  onClick={handleSendNotification}
                  disabled={loading}
                />
              </Box>
            </>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  gridTemplateColumns: { xs: "1fr", sm: " 1fr" },
                  gap: 1.5,
                  mb: 1.5,
                }}
              >
                <FormControl fullWidth>
                  <CustomSelect
                    fullWidth
                    label="Target"
                    options={[
                      { value: "individualclient", label: "Individual Client" },
                      { value: "communitygroups", label: "Community Groups" },
                    ]}
                    value={selectedTarget}
                    onChange={(
                      value: string | number | (string | number)[]
                    ) => {
                      setSelectedTarget(value);
                      setSelectedAudience([]);
                    }}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <CustomSelect
                    fullWidth
                    size="large"
                    searchable
                    multiSelect
                    label={
                      selectedTarget === "individualclient"
                        ? "Select Clients"
                        : selectedTarget === "communitygroups"
                        ? "Select Communities"
                        : "Select Audience"
                    }
                    options={
                      selectedTarget === "individualclient"
                        ? clientSearchOptions.withSelected(selectedAudience)
                        : audienceOptions.map((item) => ({
                            value: getItemId(item),
                            label:
                              item.name ||
                              item.full_name ||
                              item.title ||
                              item.fullName ||
                              `ID: ${getItemId(item)}`,
                          }))
                    }
                    {...(selectedTarget === "individualclient"
                      ? {
                          searchValue: clientSearchOptions.search,
                          onSearchValueChange: clientSearchOptions.setSearch,
                          filterLocally: false,
                          searchPlaceholder: "Search name, phone or email",
                        }
                      : {})}
                    value={selectedAudience}
                    onChange={(
                      value: string | number | (string | number)[]
                    ) => {
                      console.log("Selected audience:", value);
                      setSelectedAudience(
                        Array.isArray(value) ? value : [value]
                      );
                    }}
                    disabled={loadingAudience || !selectedTarget}
                  />
                </FormControl>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <CustomTextField
                  label={"Title"}
                  variant="outlined"
                  fullWidth
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <CustomTextField
                  label={"Message"}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </Box>

              <Box
                sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}
              >
                <CustomButton
                  label="Send Notification"
                  variant="contained"
                  sx={{ backgroundColor: "#1976d2" }}
                  onClick={handleSendNotification}
                  disabled={loading}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
      <Box sx={{ mt: { xs: 2, sm: 3 }, mb: 5 }}>
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          dateRangeConfig={{
            value: dateRange ?? undefined,
            onChange: (v: any) => setDateRange(v),
            fullWidth: false,
          }}
        />
        <CustomTable<any>
          filters={{ page: 0, size: 10, search }}
          clientSidePagination={true}
          loading={loadingNotifications}
          fields={[
            { key: "title", header: "Title" },
            { key: "message", header: "Message" },
            { key: "created_at", header: "Created At" },
            { key: "recipient", header: "Recipient" },
          ]}
          fetchFunc={async (params?: any) => {
            const q = (params && params.search) || search || "";

            const normalizedQuery = String(q || "")
              .toLowerCase()
              .trim();

            const { start: startDate, end: endDate } = getNormalizedRange(dateRange);

            const filtered = notifications.filter((r: any) => {
              if (startDate || endDate) {
                const created =
                  r && r.created_at ? new Date(r.created_at) : null;
                if (created && !isNaN(created.getTime())) {
                  if (startDate && created < startDate) return false;
                  if (endDate && created > endDate) return false;
                }
              }

              if (!normalizedQuery) return true;

              const haystack = [r.title, r.message, r.created_at, r.recipient]
                .filter(Boolean)
                .map((v: any) => String(v).toLowerCase())
                .join(" ");

              return haystack.includes(normalizedQuery);
            });

            const content = filtered;
            const totalElements = content.length;
            const pageSize = (params && params.size) || 10;
            const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));

            return {
              content,
              number: 0,
              size: pageSize,
              totalElements,
              totalPages,
            };
          }}
        />
      </Box>
    </Box>
  );
};

export default UserNotifications;
